import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useStore } from '@/context/StoreContext';
import { api, productPath } from '@/lib/api';
import { ProductCard } from '@/components/ProductCard';
import { NotifyMeDialog } from '@/components/NotifyMeDialog';
import { WishlistButton } from '@/components/WishlistButton';
import { Seo, productJsonLd } from '@/components/Seo';
import { toast } from 'sonner';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { Minus, Plus, ShieldCheck, Award, Snowflake, Truck, Lock, ArrowLeft, BellRing, Info, Flame } from 'lucide-react';

const availabilityLabel = (t, a) => ({
  in_stock: t.product.inStock, low_stock: t.product.lowStock,
  out_of_stock: t.product.outStock, on_request: t.product.onRequest,
}[a] || a);

export default function ProductDetail() {
  const { id } = useParams();
  const { t, lang, formatPrice, addToCart } = useStore();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null); setQty(1); setActiveImg(0);
    api.product(id).then(setP).catch(() => setP(false));
  }, [id]);

  if (p === false) return <div className="nw-container py-32 text-center text-nw-warmgrey">Product not found. <Link to="/collections" className="text-nw-gold">Back to collections</Link></div>;
  if (!p) return (
    <div className="nw-container py-12 grid lg:grid-cols-2 gap-16">
      <div className="bg-nw-espresso animate-pulse" style={{ aspectRatio: '4/5' }} />
      <div className="space-y-4"><div className="h-4 w-40 bg-nw-espresso animate-pulse" /><div className="h-12 w-3/4 bg-nw-espresso animate-pulse" /><div className="h-24 bg-nw-espresso animate-pulse" /></div>
    </div>
  );

  const images = p.images && p.images.length ? p.images : [null];
  const desc = lang === 'fr' ? p.description_fr : p.description_en;
  const tasting = lang === 'fr' ? p.tasting_fr : p.tasting_en;
  const priceNote = lang === 'fr' ? p.price_note_fr : p.price_note_en;
  const soldOut = p.stock <= 0 || p.availability === 'out_of_stock';
  const altBase = `${p.name} — ${p.estate}, ${p.region}, ${p.format}`;

  const add = () => { addToCart(p, qty); toast.success(t.common.addedToBasket); };

  const infoSections = [
    { key: 'auth', icon: ShieldCheck, title: t.product.authenticity,
      body: lang === 'fr' ? "Chaque bouteille est authentifiée par nos experts et accompagnée d'un certificat. Traçabilité complète depuis le domaine." : 'Every bottle is authenticated by our experts and accompanied by a certificate. Full traceability from the estate.' },
    { key: 'prov', icon: Award, title: t.product.provenance,
      body: lang === 'fr' ? 'Acquisition en primeur ou auprès de caves privées sélectionnées, avec historique de conservation documenté.' : 'Sourced en primeur or from selected private cellars, with documented storage history.' },
    { key: 'store', icon: Snowflake, title: t.product.storage,
      body: lang === 'fr' ? "Conservation en cave professionnelle à température et hygrométrie contrôlées, à l'abri de la lumière." : 'Kept in a professional cellar under controlled temperature and humidity, away from light.' },
    { key: 'deliv', icon: Truck, title: t.product.delivery,
      body: lang === 'fr' ? 'Expédition mondiale en transport spécialisé et suivi. Emballage isotherme sur demande.' : 'Worldwide shipping via specialised, tracked couriers. Temperature-controlled packaging on request.' },
    { key: 'ins', icon: Lock, title: t.product.insurance,
      body: lang === 'fr' ? 'Assurance pleine valeur disponible à la commande, couvrant perte et dommage en transit.' : 'Full-value insurance available at checkout, covering loss and damage in transit.' },
  ];

  const seoTitle = `${p.name} – ${p.subcategory}${p.category === 'spirit' ? '' : ' Wine'}`;
  const seoDesc = desc || `${p.name}, ${p.estate}. ${p.region}. ${p.format}.`;

  return (
    <div className="bg-nw-black">
      <Seo title={seoTitle} description={seoDesc} path={productPath(p)} image={images[0]} type="product" lang={lang}
        jsonLd={productJsonLd(p, window.location.origin, lang)} />
      <div className="nw-container pt-8">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[11px] tracking-luxe uppercase text-nw-warmgrey">
          <Link to="/collections" className="inline-flex items-center gap-2 nw-link"><ArrowLeft size={14} /> {t.catalog.title}</Link>
          <span aria-hidden="true">/</span>
          <Link to={`/collections?subcategory=${encodeURIComponent(p.subcategory)}`} className="nw-link">{p.subcategory}</Link>
        </nav>
      </div>
      <div className="nw-container py-8 lg:py-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div className="lg:sticky lg:top-[92px] self-start">
            <div className="relative bg-nw-espresso border border-nw-gold/15 overflow-hidden" style={{ aspectRatio: '4/5' }}>
              {images[activeImg] ? (
                <img src={images[activeImg]} alt={altBase} className="w-full h-full object-cover" fetchPriority="high" />
              ) : <div className="w-full h-full" />}
              <WishlistButton product={p} className="absolute top-4 right-4" />
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-3">
                {images.map((im, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} aria-label={`${p.name} — ${i + 1}`}
                    className={`w-20 h-24 overflow-hidden border ${activeImg === i ? 'border-nw-gold' : 'border-nw-gold/20'}`}>
                    <img src={im} alt={`${p.name} — ${lang === 'fr' ? 'vue' : 'view'} ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="nw-eyebrow text-nw-gold">{p.subcategory} · {p.region}</div>
              {p.recently_restocked && (
                <span data-testid="product-restocked-ribbon" className="inline-flex items-center gap-1.5 text-[9px] tracking-luxe uppercase text-nw-black bg-nw-gold px-2 py-1">
                  <span className="h-1 w-1 rounded-full bg-nw-black/70" aria-hidden="true" />{t.product.backInCellar}
                </span>
              )}
            </div>
            <h1 data-testid="product-title" className="font-serif text-3xl sm:text-4xl lg:text-5xl text-nw-ivory mt-3 leading-tight">{p.name}</h1>
            {desc && <p className="mt-5 text-nw-warmgrey text-sm leading-relaxed">{desc}</p>}

            <div className="mt-7 grid grid-cols-2 gap-y-3 gap-x-6 text-sm border-t border-b border-nw-gold/15 py-6">
              <Row label={t.product.estate} value={p.estate} />
              <Row label={t.product.vintage} value={p.vintage} />
              <Row label={t.product.region} value={p.region} />
              <Row label={t.product.format} value={p.format} />
              <Row label={t.product.abv} value={`${p.abv}%`} />
              <Row label={t.product.reference} value={p.reference_id} />
              <Row label={t.product.rarity} value={p.rarity} cap />
              <Row label={t.product.availability} value={soldOut ? t.product.outStock : availabilityLabel(t, p.availability)} />
            </div>

            <div className="mt-7">
              <div className="nw-eyebrow">{soldOut ? t.product.outStock : ''}</div>
              <div data-testid="product-price" className="font-serif text-nw-gold text-3xl mt-1">
                {formatPrice(p.price_eur)}
              </div>
              {priceNote && (
                <p data-testid="product-price-note" className="mt-3 text-[12px] text-nw-warmgrey leading-relaxed flex gap-2 border border-nw-gold/20 bg-nw-espresso px-3 py-2">
                  <Info size={14} className="text-nw-gold shrink-0 mt-0.5" /> <span>{priceNote}</span>
                </p>
              )}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <div className="flex items-center border border-nw-gold/30 rounded-[3px] h-12">
                <button data-testid="product-qty-decrease" aria-label="-" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-12 h-full text-nw-ivory hover:text-nw-gold flex items-center justify-center"><Minus size={16} /></button>
                <span data-testid="product-qty" className="w-10 text-center text-nw-ivory">{qty}</span>
                <button data-testid="product-qty-increase" aria-label="+" onClick={() => setQty((q) => Math.min(Math.max(p.stock, 1), q + 1))} className="w-12 h-full text-nw-ivory hover:text-nw-gold flex items-center justify-center"><Plus size={16} /></button>
              </div>
              <button data-testid="product-add-to-basket-button" onClick={add} disabled={soldOut} className="nw-btn-gold flex-1 h-12 disabled:opacity-50 disabled:cursor-not-allowed">{t.product.addToBasket}</button>
            </div>
            {!soldOut && p.stock > 0 && p.stock <= 5 && (
              <div data-testid="product-low-stock" className="mt-3 inline-flex items-center gap-2 text-[12px] tracking-luxe uppercase text-nw-gold">
                <Flame size={14} /> {t.product.onlyLeft.replace('{n}', p.stock)}
              </div>
            )}
            {soldOut && (
              <div className="mt-4">
                <div className="text-sm text-nw-warmgrey mb-4 border-l-2 border-nw-gold/50 pl-4">{t.product.soldOutNote}</div>
                <NotifyMeDialog product={p} trigger={<button data-testid="product-notify-me-button" className="nw-btn-ivory w-full h-12"><BellRing size={16} /> {t.product.notifyMe}</button>} />
              </div>
            )}
            <div className="mt-4"><WishlistButton product={p} variant="button" className="w-full sm:w-auto" /></div>

            <p className="mt-4 text-[11px] text-nw-warmgrey/70 tracking-wide">{t.common.vat}</p>

            <Accordion data-testid="product-details-tabs" type="single" collapsible defaultValue="auth" className="mt-8">
              {tasting && (
                <AccordionItem value="tasting" className="border-nw-gold/15">
                  <AccordionTrigger className="text-[11px] tracking-luxe uppercase text-nw-ivory hover:text-nw-gold hover:no-underline">{t.product.tasting}</AccordionTrigger>
                  <AccordionContent className="text-sm text-nw-warmgrey leading-relaxed">{tasting}</AccordionContent>
                </AccordionItem>
              )}
              {infoSections.map((s) => (
                <AccordionItem key={s.key} value={s.key} className="border-nw-gold/15">
                  <AccordionTrigger className="text-[11px] tracking-luxe uppercase text-nw-ivory hover:text-nw-gold hover:no-underline">
                    <span className="flex items-center gap-2"><s.icon size={15} className="text-nw-gold" /> {s.title}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-nw-warmgrey leading-relaxed">{s.body}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        {p.related && p.related.length > 0 && (
          <section className="mt-20" aria-labelledby="related-title">
            <h2 id="related-title" className="font-serif text-2xl text-nw-ivory text-center mb-8">{t.product.related}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {p.related.map((r, i) => <ProductCard key={r.id} product={r} index={i} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value, cap }) => (
  <div>
    <div className="text-[10px] tracking-luxe uppercase text-nw-warmgrey">{label}</div>
    <div className={`text-nw-ivory mt-0.5 ${cap ? 'capitalize' : ''}`}>{value || '—'}</div>
  </div>
);
